import React from "react"
import ReactDOM from "react-dom/client"
import { Provider } from "react-redux"
import { PersistGate } from "redux-persist/integration/react"
import { Loader2 } from "lucide-react"
import App from "./App"
import Initializer from "./components/Initializer/Initializer"
import "./main.css"
import { persistor, store } from "store/store"
import "./translations/i18n.ts"
import queryClient from "providers/queryClient"
import { QueryClientProvider } from "@tanstack/react-query"
import * as Sentry from "@sentry/react"
import { isDevelopment, typedEnv } from "./config/typedEnv"
import { ClerkProvider } from "@clerk/react"
import { ClerkFallbackBoundary } from "components/ClerkFallbackBoundary"
import { clearDeprecatedLegacyAuthKeys } from "utils/authSessionStorage"
import { validatePlatformServiceEndpoints } from "services/platformApiAuth"
import { initPlatformAnalytics } from "services/platformAnalytics"
import { captureMarketingAttributionFromUrl } from "services/marketingAttribution"

const resolveRedirectUrl = (value?: string) => {
  if (!value) return undefined

  try {
    return new URL(value, window.location.origin).toString()
  } catch {
    return undefined
  }
}

const clerkSignInForceRedirectUrl = resolveRedirectUrl(
  typedEnv.VITE_CLERK_SIGN_IN_FORCE_REDIRECT_URL
)
const clerkSignUpForceRedirectUrl = resolveRedirectUrl(
  typedEnv.VITE_CLERK_SIGN_UP_FORCE_REDIRECT_URL
)
const clerkSignInFallbackRedirectUrl = resolveRedirectUrl(
  typedEnv.VITE_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL
)
const clerkSignUpFallbackRedirectUrl = resolveRedirectUrl(
  typedEnv.VITE_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL
)

if (!isDevelopment)
  Sentry.init({
    dsn: "https://b80d4819e2e6b4d7d8db700231d19e12@o1346587.ingest.us.sentry.io/4508805588910080",
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: [/\.sentry\.io$/],
    environment: typedEnv.VITE_SENTRY_ENV,
    beforeSend(event, hint) {
      const error = hint.originalException
      if (error instanceof Error && error.message.startsWith("HTTP error:")) return null

      return event
    },
  })

clearDeprecatedLegacyAuthKeys()
validatePlatformServiceEndpoints()
captureMarketingAttributionFromUrl()
initPlatformAnalytics()

ReactDOM.createRoot(document.getElementById("root")!).render(
  <Provider store={store}>
    <PersistGate
      loading={
        <div className="flex h-screen w-screen items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      }
      persistor={persistor}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkFallbackBoundary>
          <ClerkProvider
            publishableKey={typedEnv.VITE_CLERK_PUBLISHABLE_KEY}
            signInUrl={typedEnv.VITE_CLERK_SIGN_IN_URL}
            signUpUrl={typedEnv.VITE_CLERK_SIGN_UP_URL}
            signInForceRedirectUrl={clerkSignInForceRedirectUrl}
            signUpForceRedirectUrl={clerkSignUpForceRedirectUrl}
            signInFallbackRedirectUrl={clerkSignInFallbackRedirectUrl}
            signUpFallbackRedirectUrl={clerkSignUpFallbackRedirectUrl}
          >
            <Initializer>
              <React.StrictMode>
                <App />
              </React.StrictMode>
            </Initializer>
          </ClerkProvider>
        </ClerkFallbackBoundary>
      </QueryClientProvider>
    </PersistGate>
  </Provider>
)
